// Base
import { BaseApi } from 'api/base.api'

// Interface
import {
  ICertificationWebpageRelevantProofApi,
  ICertificationWebpageRelevantProofApiRequest,
  ICertificationWebpageRelevantProofApiListResponse,
  ICertificationWebpageRelevantProofApiNullResponse,
  ICertificationWebpageRelevantProofApiGetListAttrs,
  ICertificationWebpageRelevantProofApiUploadDocumentAttrs,
  ICertificationWebpageRelevantProofApiUpdateAttrs,
  ICertificationWebpageRelevantProofApiDeleteDocumentAttrs,
  ICertificationWebpageRelevantProofApiUploadDocumentResponse,
  ICertificationWebpageRelevantProofApiDownloadDocumentAttrs,
  ICertificationWebpageRelevantProofApiDownloadDocumentResponse,
} from 'interfaces/webpage/certification/registration/precondition'

// Constant
import { ApiConstantHc, ApiConstantHcBase } from 'constant'

// Utils
import { axios_renderHcUrl } from 'utils'

export class CertificationWebpageRelevantProofApi
  extends BaseApi<ICertificationWebpageRelevantProofApi>
  implements ICertificationWebpageRelevantProofApiRequest
{
  readonly url = ApiConstantHc.CERTIFICATION_REGISTRATION_PRECONDITION_RELEVANT_PROOF_URL
  private certificationRegistrationRelevantProofUrl = axios_renderHcUrl(this.url, ApiConstantHcBase.ASSESSMENT)

  /**
   * @description Get general proof list
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageRelevantProofApiGetListAttrs} payload
   *
   * @return {Promise<ICertificationWebpageRelevantProofApiListResponse>} Promise<ICertificationWebpageRelevantProofApiListResponse>
   */
  async certificationWebpageRelevantProof_getList(
    payload: ICertificationWebpageRelevantProofApiGetListAttrs,
  ): Promise<ICertificationWebpageRelevantProofApiListResponse> {
    return this.POST(
      `${this.certificationRegistrationRelevantProofUrl}/list/${payload.params.assessmentId}`,
      {},
      { ...payload.requestConfig },
    )
  }

  /**
   * @description Update general proof
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageRelevantProofApiUpdateAttrs} payload
   *
   * @return {Promise<ICertificationWebpageRelevantProofApiNullResponse>} Promise<ICertificationWebpageRelevantProofApiNullResponse>
   */
  async certificationWebpageRelevantProof_update(
    payload: ICertificationWebpageRelevantProofApiUpdateAttrs,
  ): Promise<ICertificationWebpageRelevantProofApiNullResponse> {
    return this.POST(`${this.certificationRegistrationRelevantProofUrl}/update/${payload.params.assessmentId}`, payload.body, {
      ...payload.requestConfig,
    })
  }

  /**
   * @description Upload general proof document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageRelevantProofApiUploadDocumentAttrs} payload
   *
   * @return {Promise<ICertificationWebpageRelevantProofApiUploadDocumentResponse>} Promise<ICertificationWebpageRelevantProofApiUploadDocumentResponse>
   */
  async certificationWebpageRelevantProof_uploadDocument(
    payload: ICertificationWebpageRelevantProofApiUploadDocumentAttrs,
  ): Promise<ICertificationWebpageRelevantProofApiUploadDocumentResponse> {
    const formData = new FormData()
    formData.append('document', payload.body.document)
    formData.append('answerCode', payload.body.answerCode)

    if (payload.body.transactionFileId) formData.append('transactionFileId', payload.body.transactionFileId.toString())

    return this.POST(
      `${this.certificationRegistrationRelevantProofUrl}/uploadDocument/${payload.params.assessmentId}`,
      formData,
      { ...payload.requestConfig, headers: { 'Content-Type': 'multipart/form-data' } },
    )
  }

  /**
   * @description Delete general proof document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageRelevantProofApiDeleteDocumentAttrs}
   *
   * @return {Promise<ICertificationWebpageRelevantProofApiNullResponse>} Promise<ICertificationWebpageRelevantProofApiNullResponse>
   */
  async certificationWebpageRelevantProof_deleteDocument(
    payload: ICertificationWebpageRelevantProofApiDeleteDocumentAttrs,
  ): Promise<ICertificationWebpageRelevantProofApiNullResponse> {
    return this.POST(
      `${this.certificationRegistrationRelevantProofUrl}/deleteDocument/${payload.params.userFileTransactionId}`,
      {},
      { ...payload.requestConfig },
    )
  }

  /**
   * @description Download document of relevant proof
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageRelevantProofApiDownloadDocumentAttrs} payload
   *
   * @return {Promise<ICertificationWebpageRelevantProofApiDownloadDocumentResponse>} Promise<ICertificationWebpageRelevantProofApiDownloadDocumentResponse>
   */
  async certificationWebpageRelevantProof_downloadDocument(
    payload: ICertificationWebpageRelevantProofApiDownloadDocumentAttrs,
  ): Promise<ICertificationWebpageRelevantProofApiDownloadDocumentResponse> {
    return this.POST(
      `${this.certificationRegistrationRelevantProofUrl}/download/${payload.params.blobId}`,
      {},
      { ...payload.requestConfig, responseType: 'blob' },
    )
  }
}
